/**
 * Price + indicator alerts (RSI, MACD, SMA…). Alerts stay active until the user deletes them.
 * On-screen toast (bottom) + system notification (background tabs, via the service worker).
 */

import {
  INDICATOR_DEFS,
  evalIndicator,
  indicatorDef,
  indicatorLabel,
  type Bar,
} from "./indicators";
import { getPushEndpoint, requestNotifications, showLocalNotification } from "./push";

export type AlertCond = "cross_up" | "cross_down" | "above" | "below";
export type AlertFreq = "once" | "every";
/** "price" or any INDICATOR_DEFS id (legacy "rsi" → rsi_14). */
export type AlertSource = string;

export type PriceAlert = {
  id: string;
  symbol: string;
  /** Alert level (price or indicator value). */
  price: number;
  cond: AlertCond;
  freq: AlertFreq;
  source: AlertSource;
  /** @deprecated kept for migrate; use source id instead */
  rsiPeriod?: number;
  name: string;
  createdAt: number;
  active: boolean;
  triggeredAt?: number;
  triggerCount: number;
  shapeId?: string;
};

export type AlertLog = {
  id: string;
  alertId: string;
  symbol: string;
  price: number;
  ltp: number;
  cond: AlertCond;
  source?: AlertSource;
  at: number;
  message: string;
};

const STORE_KEY = "ki_charts_alerts_v2";
const STORE_KEY_LEGACY = "ki_charts_alerts_v1";
const LOG_KEY = "ki_charts_alert_log_v1";
const SERVER_SINCE_KEY = "ki_charts_alert_srv_since_v1";
const MAX_LOG = 80;

type Deps = {
  getWidget: () => any;
  getActiveSymbol: () => string;
  getLtp: (symbol: string) => number;
  /** OHLCV bars oldest→newest (prefer 5m tip). */
  getBars: (symbol: string) => Bar[];
  /** Warm tip/history for indicator alerts when bars are thin. */
  ensureBars?: (symbol: string) => void;
  /** @deprecated */
  getCloses?: (symbol: string) => number[];
  openSymbol: (symbol: string) => void;
  showToast?: (msg: string, kind?: "ok" | "warn" | "info") => void;
};

function uid(): string {
  return "a" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function normalizeSource(s: string): string {
  if (!s || s === "price") return "price";
  if (s === "rsi") return "rsi_14";
  return s;
}

function migrateAlerts(raw: any[]): PriceAlert[] {
  return (raw || []).map(function (a) {
    return {
      id: String(a.id || uid()),
      symbol: String(a.symbol || ""),
      price: Number(a.price) || 0,
      cond: (a.cond as AlertCond) || "cross_up",
      freq: (a.freq as AlertFreq) || "once",
      source: normalizeSource(String(a.source || "price")),
      rsiPeriod: Number(a.rsiPeriod) || 14,
      name: String(a.name || ""),
      createdAt: Number(a.createdAt) || Date.now(),
      active: true,
      triggeredAt: a.triggeredAt,
      triggerCount: Number(a.triggerCount) || 0,
      shapeId: a.shapeId,
    };
  });
}

function loadAlerts(): PriceAlert[] {
  try {
    let raw = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    if (!Array.isArray(raw)) {
      raw = JSON.parse(localStorage.getItem(STORE_KEY_LEGACY) || "[]");
    }
    return migrateAlerts(Array.isArray(raw) ? raw : []);
  } catch {
    return [];
  }
}

function saveAlerts(list: PriceAlert[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function loadLog(): AlertLog[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function saveLog(list: AlertLog[]): void {
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(list.slice(0, MAX_LOG)));
  } catch {
    /* ignore */
  }
}

function condLabel(c: AlertCond): string {
  if (c === "cross_up") return "Crossing Up";
  if (c === "cross_down") return "Crossing Down";
  if (c === "above") return "Greater Than";
  return "Less Than";
}

function beep(): void {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.value = 0.055;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    setTimeout(function () {
      o.stop();
      ctx.close();
    }, 220);
  } catch {
    /* ignore */
  }
}

function ensureNotifyPermission(): void {
  if ("Notification" in window && Notification.permission === "default") requestNotifications();
}

function sameDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function barsFor(deps: Deps, symbol: string): Bar[] {
  if (deps.getBars) {
    try {
      return deps.getBars(symbol) || [];
    } catch {
      return [];
    }
  }
  if (deps.getCloses) {
    return (deps.getCloses(symbol) || []).map(function (c) {
      return { open: c, high: c, low: c, close: c, volume: 0 };
    });
  }
  return [];
}

export function initAlerts(deps: Deps): {
  onQuote: (symbol: string, ltp: number) => void;
  openCreate: (opts?: { symbol?: string; price?: number }) => void;
  redrawLines: () => void;
  refreshBadge: () => void;
} {
  let alerts = loadAlerts();
  let log = loadLog();
  const prevVal: Record<string, number> = {};
  const lastFireAt: Record<string, number> = {};
  /** True once the server has accepted this browser's alerts (push works with the site closed). */
  let serverOn = false;
  let syncTimer = 0;
  let syncing = false;

  const panel = document.getElementById("cxAlertsPanel");
  const backdrop = document.getElementById("cxAlertsBackdrop");
  const listEl = document.getElementById("cxAlertsList");
  const logEl = document.getElementById("cxAlertsLog");
  const badge = document.getElementById("cxAlertsBadge");
  const form = document.getElementById("cxAlertForm") as HTMLFormElement | null;
  const symInput = document.getElementById("cxAlertSym") as HTMLInputElement | null;
  const priceInput = document.getElementById("cxAlertPrice") as HTMLInputElement | null;
  const condSel = document.getElementById("cxAlertCond") as HTMLSelectElement | null;
  const freqSel = document.getElementById("cxAlertFreq") as HTMLSelectElement | null;
  const srcSel = document.getElementById("cxAlertSource") as HTMLSelectElement | null;
  const nameInput = document.getElementById("cxAlertName") as HTMLInputElement | null;
  const valueLabel = document.getElementById("cxAlertValueLabel");
  const useBtn = document.getElementById("cxAlertUseLtp");
  const toastHost = document.getElementById("cxAlertToasts");
  const notifyStatus = document.getElementById("cxAlertNotifyStatus");
  const liveEl = document.getElementById("cxAlertLiveVal");

  /** Fill Source select with Price + indicator groups. */
  function populateSources(): void {
    if (!srcSel) return;
    const cur = normalizeSource(srcSel.value || "price");
    const groups: Record<string, string> = {
      price: "Price",
      ma: "Moving averages",
      oscillator: "Oscillators",
      volatility: "Volatility",
      volume: "Volume",
    };
    const order = ["price", "ma", "oscillator", "volatility", "volume"];
    srcSel.innerHTML = "";
    order.forEach(function (g) {
      const opts = INDICATOR_DEFS.filter(function (d) {
        return d.group === g;
      });
      if (!opts.length) return;
      const og = document.createElement("optgroup");
      og.label = groups[g] || g;
      opts.forEach(function (d) {
        const o = document.createElement("option");
        o.value = d.id;
        o.textContent = d.label;
        og.appendChild(o);
      });
      srcSel.appendChild(og);
    });
    srcSel.value = BY_ID_HAS(cur) ? cur : "price";
  }

  function BY_ID_HAS(id: string): boolean {
    return !!indicatorDef(id) || id === "price";
  }

  function refreshBadge(): void {
    if (!badge) return;
    const n = alerts.filter((a) => a.active).length;
    badge.textContent = String(n);
    badge.hidden = n === 0;
  }

  function updateNotifyStatus(): void {
    if (!notifyStatus) return;
    if (!("Notification" in window)) {
      notifyStatus.textContent = "Browser notifications unsupported";
      return;
    }
    const p = Notification.permission;
    if (p === "granted" && serverOn) notifyStatus.textContent = "Notifications are ON — alerts reach you even when Bharat Charts is closed.";
    else if (p === "granted") notifyStatus.textContent = "Notifications are ON — alerts reach you even when this tab is in the background.";
    else if (p === "denied") notifyStatus.textContent = "Notifications are blocked — allow them in your browser's site settings.";
    else notifyStatus.textContent = "Click “Turn on notifications” to get alerts in the background.";
  }

  function toast(msg: string, kind: "ok" | "warn" | "info" = "ok"): void {
    if (!toastHost) return;
    const el = document.createElement("div");
    el.className = "cx-alert-toast cx-alert-toast--" + kind + " is-in";
    el.innerHTML =
      '<div class="cx-alert-toast-title">Alert</div><div class="cx-alert-toast-body">' +
      escapeHtml(msg) +
      "</div>";
    toastHost.appendChild(el);
    setTimeout(function () {
      el.classList.add("is-out");
      setTimeout(function () {
        el.remove();
      }, 320);
    }, 6500);
  }

  function notifyBrowser(title: string, body: string, tag?: string): void {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      requestNotifications();
      return;
    }
    showLocalNotification(title, body, tag || "bc-alert-" + Date.now());
  }

  /** Mirrors alerts to the server (keyed by this browser's push subscription) and pulls in server fires. */
  function scheduleSync(delay = 800): void {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncServer, delay);
  }

  async function syncServer(): Promise<void> {
    if (syncing) {
      scheduleSync(1500);
      return;
    }
    const endpoint = await getPushEndpoint();
    if (!endpoint) {
      serverOn = false;
      updateNotifyStatus();
      return;
    }
    syncing = true;
    try {
      let since = 0;
      try {
        since = Number(localStorage.getItem(SERVER_SINCE_KEY)) || 0;
      } catch {
        /* ignore */
      }
      const res = await fetch("/api/alerts.php?action=sync", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint,
          since,
          alerts: alerts.map(function (a) {
            return {
              id: a.id,
              symbol: a.symbol,
              price: a.price,
              cond: a.cond,
              freq: a.freq,
              source: normalizeSource(a.source),
              name: a.name,
              createdAt: a.createdAt,
              triggeredAt: a.triggeredAt || 0,
            };
          }),
        }),
      });
      const j = await res.json().catch(function () {
        return null;
      });
      serverOn = !!(j && j.ok);
      if (serverOn) mergeServer(j, since);
    } catch {
      serverOn = false;
    } finally {
      syncing = false;
      updateNotifyStatus();
    }
  }

  function mergeServer(j: any, since: number): void {
    let changed = false;
    const st = j.state || {};
    alerts.forEach(function (a) {
      const s = st[a.id];
      if (!s) return;
      if (Number(s.triggeredAt) > (a.triggeredAt || 0)) {
        a.triggeredAt = Number(s.triggeredAt);
        changed = true;
      }
      if (Number(s.triggerCount) > (a.triggerCount || 0)) {
        a.triggerCount = Number(s.triggerCount);
        changed = true;
      }
    });
    let maxAt = since;
    (Array.isArray(j.fires) ? j.fires : []).forEach(function (f: any) {
      const at = Number(f.at) || 0;
      if (at > maxAt) maxAt = at;
      const dup = log.some(function (e) {
        return e.id === f.id || (e.alertId === f.alertId && Math.abs(e.at - at) < 90000);
      });
      if (dup) return;
      log.push({
        id: String(f.id),
        alertId: String(f.alertId),
        symbol: String(f.symbol),
        price: Number(f.price),
        ltp: Number(f.value),
        cond: f.cond as AlertCond,
        source: String(f.source),
        at,
        message: String(f.message) + " · sent to your devices",
      });
      changed = true;
    });
    if (maxAt > since) {
      try {
        localStorage.setItem(SERVER_SINCE_KEY, String(maxAt));
      } catch {
        /* ignore */
      }
    }
    if (!changed) return;
    log.sort(function (a, b) {
      return b.at - a.at;
    });
    saveLog(log);
    saveAlerts(alerts);
    renderList();
    renderLog();
  }

  function sourceLabel(a: PriceAlert): string {
    return indicatorLabel(normalizeSource(a.source));
  }

  function formatLevel(a: PriceAlert): string {
    const src = normalizeSource(a.source);
    if (src === "price") return formatPx(a.price);
    const d = indicatorDef(src);
    if (d && d.bounded01) return String(a.price);
    if (Math.abs(a.price) >= 1000) return formatPx(a.price);
    return String(Math.round(a.price * 10000) / 10000);
  }

  function currentValue(symbol: string, source: string): number | null {
    const src = normalizeSource(source);
    const ltp = deps.getLtp(symbol);
    if (src === "price") return ltp > 0 ? ltp : null;
    return evalIndicator(src, barsFor(deps, symbol), ltp > 0 ? ltp : undefined);
  }

  function syncValueUi(): void {
    const src = normalizeSource(srcSel?.value || "price");
    const def = indicatorDef(src);
    if (valueLabel) valueLabel.textContent = src === "price" ? "Price" : "Level";
    if (useBtn) useBtn.textContent = src === "price" ? "Use LTP" : "Use current";
    if (priceInput) {
      if (def && def.bounded01 && def.defaultLevel != null) {
        priceInput.min = String(src === "williams_r" ? -100 : 0);
        priceInput.max = "100";
        if (!priceInput.value) priceInput.value = String(def.defaultLevel);
      } else {
        priceInput.removeAttribute("max");
        priceInput.min = src === "williams_r" ? "-100" : "";
        if (def && def.defaultLevel != null && !priceInput.value) {
          priceInput.value = String(def.defaultLevel);
        }
      }
    }
    updateLiveVal();
  }

  function updateLiveVal(): void {
    if (!liveEl) return;
    const sym = (symInput?.value || deps.getActiveSymbol() || "").trim();
    const src = normalizeSource(srcSel?.value || "price");
    if (!sym) {
      liveEl.textContent = "";
      return;
    }
    const v = currentValue(sym, src);
    if (v == null || !isFinite(v)) {
      liveEl.textContent = "Live: — (need more bars)";
      return;
    }
    liveEl.textContent =
      "Live " + indicatorLabel(src) + ": " + (src === "price" ? formatPx(v) : String(Math.round(v * 10000) / 10000));
  }

  async function drawLine(alert: PriceAlert): Promise<void> {
    if (normalizeSource(alert.source) !== "price") return;
    const w = deps.getWidget();
    if (!w || typeof w.activeChart !== "function") return;
    const chart = w.activeChart();
    if (!chart || typeof chart.createShape !== "function") return;
    const active = String(deps.getActiveSymbol() || "").toUpperCase();
    if (active !== String(alert.symbol).toUpperCase()) return;
    try {
      if (alert.shapeId && typeof chart.removeEntity === "function") {
        try {
          chart.removeEntity(alert.shapeId);
        } catch {
          /* ignore */
        }
      }
      const id = await chart.createShape(
        { price: alert.price },
        {
          shape: "horizontal_line",
          text: "🔔 " + (alert.name || condLabel(alert.cond) + " " + alert.price),
          lock: false,
          disableSelection: false,
          disableSave: true,
          overrides: {
            linecolor: "#4caf50",
            linestyle: 2,
            linewidth: 1,
            showLabel: true,
            textcolor: "#4caf50",
            fontsize: 11,
          },
        }
      );
      alert.shapeId = id ? String(id) : undefined;
      saveAlerts(alerts);
    } catch {
      /* ignore */
    }
  }

  async function removeLine(alert: PriceAlert): Promise<void> {
    if (!alert.shapeId) return;
    const w = deps.getWidget();
    if (!w || typeof w.activeChart !== "function") return;
    try {
      w.activeChart().removeEntity(alert.shapeId);
    } catch {
      /* ignore */
    }
    alert.shapeId = undefined;
  }

  function redrawLines(): void {
    const active = String(deps.getActiveSymbol() || "").toUpperCase();
    alerts.forEach(function (a) {
      if (!a.active || normalizeSource(a.source) !== "price") return;
      if (String(a.symbol).toUpperCase() === active) drawLine(a);
    });
  }

  function renderList(): void {
    if (!listEl) return;
    const active = alerts.filter((a) => a.active);
    if (!active.length) {
      listEl.innerHTML =
        '<div class="cx-alerts-empty">No active alerts. Create Price or indicator alerts (Crossing Up / Down, Greater / Less).</div>';
      return;
    }
    listEl.innerHTML = active
      .map(function (a) {
        return (
          '<div class="cx-alert-row" data-id="' +
          a.id +
          '">' +
          '<div class="cx-alert-row-main">' +
          "<strong>" +
          escapeHtml(a.symbol) +
          "</strong>" +
          '<span class="cx-alert-meta">' +
          escapeHtml(sourceLabel(a)) +
          " · " +
          escapeHtml(condLabel(a.cond)) +
          " · " +
          escapeHtml(formatLevel(a)) +
          (a.name ? " · " + escapeHtml(a.name) : "") +
          "</span>" +
          '<span class="cx-alert-meta">' +
          (a.freq === "once" ? "Once a day" : "Every time") +
          (a.triggerCount ? " · fired " + a.triggerCount + "×" : "") +
          "</span>" +
          "</div>" +
          '<div class="cx-alert-row-acts">' +
          '<button type="button" class="cx-alert-mini" data-act="goto">Chart</button>' +
          '<button type="button" class="cx-alert-mini is-danger" data-act="del">Delete</button>' +
          "</div></div>"
        );
      })
      .join("");
  }

  function renderLog(): void {
    if (!logEl) return;
    if (!log.length) {
      logEl.innerHTML = '<div class="cx-alerts-empty">Alert log is empty.</div>';
      return;
    }
    logEl.innerHTML = log
      .slice(0, 80)
      .map(function (e) {
        const t = new Date(e.at);
        const ts =
          t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) +
          " " +
          t.toLocaleDateString([], { day: "2-digit", month: "short" });
        return (
          '<div class="cx-alert-log-row" data-log-id="' +
          escapeHtml(e.id) +
          '">' +
          '<div class="cx-alert-log-main">' +
          '<span class="cx-alert-log-ts">' +
          escapeHtml(ts) +
          "</span>" +
          "<strong>" +
          escapeHtml(e.symbol) +
          "</strong> " +
          escapeHtml(e.message) +
          "</div>" +
          '<button type="button" class="cx-alert-mini is-danger" data-act="log-del" title="Delete">✕</button>' +
          "</div>"
        );
      })
      .join("");
  }

  function clearLog(): void {
    if (!log.length) return;
    if (!window.confirm("Clear the entire alert log?")) return;
    log = [];
    saveLog(log);
    renderLog();
    toast("Alert log cleared", "info");
  }

  function deleteLogEntry(id: string): void {
    log = log.filter(function (e) {
      return e.id !== id;
    });
    saveLog(log);
    renderLog();
  }

  function openPanel(tab?: string): void {
    ensureNotifyPermission();
    updateNotifyStatus();
    if (panel) {
      panel.classList.add("is-open");
      panel.removeAttribute("hidden");
      panel.setAttribute("aria-hidden", "false");
    }
    if (backdrop) {
      backdrop.classList.add("is-open");
      backdrop.removeAttribute("hidden");
      backdrop.setAttribute("aria-hidden", "false");
    }
    if (symInput && !symInput.value) symInput.value = deps.getActiveSymbol() || "";
    syncValueUi();
    if (priceInput && !priceInput.value) {
      const src = normalizeSource(srcSel?.value || "price");
      const def = indicatorDef(src);
      if (def && def.defaultLevel != null) priceInput.value = String(def.defaultLevel);
      else {
        const ltp = deps.getLtp(symInput ? symInput.value : deps.getActiveSymbol());
        if (ltp > 0) priceInput.value = String(ltp);
      }
    }
    if (tab) setTab(tab);
    renderList();
    renderLog();
    refreshBadge();
  }

  function closePanel(): void {
    if (panel) {
      panel.classList.remove("is-open");
      panel.setAttribute("hidden", "");
      panel.setAttribute("aria-hidden", "true");
    }
    if (backdrop) {
      backdrop.classList.remove("is-open");
      backdrop.setAttribute("hidden", "");
      backdrop.setAttribute("aria-hidden", "true");
    }
  }

  function setTab(id: string): void {
    document.querySelectorAll("[data-alert-tab]").forEach(function (btn) {
      btn.classList.toggle("is-on", (btn as HTMLElement).getAttribute("data-alert-tab") === id);
    });
    document.querySelectorAll("[data-alert-pane]").forEach(function (pane) {
      const on = (pane as HTMLElement).getAttribute("data-alert-pane") === id;
      (pane as HTMLElement).hidden = !on;
    });
  }

  function openCreate(opts?: { symbol?: string; price?: number }): void {
    openPanel("create");
    if (symInput) symInput.value = opts?.symbol || deps.getActiveSymbol() || "";
    const src = normalizeSource(srcSel?.value || "price");
    if (src === "price") {
      const px = opts?.price || deps.getLtp(symInput ? symInput.value : "") || 0;
      if (priceInput && px > 0) priceInput.value = String(px);
    }
    if (nameInput) nameInput.value = "";
    ensureNotifyPermission();
    updateLiveVal();
  }

  function createFromForm(): void {
    if (!symInput || !priceInput || !condSel || !freqSel) return;
    const symbol = String(symInput.value || "").trim().toUpperCase();
    const price = Number(priceInput.value);
    const source = normalizeSource(srcSel?.value || "price");
    if (!symbol || !isFinite(price)) {
      toast("Enter symbol and a valid level", "warn");
      return;
    }
    const def = indicatorDef(source);
    if (def && def.bounded01 && source !== "williams_r" && (price < 0 || price > 100)) {
      toast("Level must be between 0 and 100 for " + def.label, "warn");
      return;
    }
    if (source === "williams_r" && (price < -100 || price > 0)) {
      toast("Williams %R level must be between −100 and 0", "warn");
      return;
    }
    if (source === "price" && !(price > 0)) {
      toast("Enter a valid price", "warn");
      return;
    }
    if (source !== "price" && deps.ensureBars) {
      try {
        deps.ensureBars(symbol);
      } catch {
        /* ignore */
      }
    }
    const live = currentValue(symbol, source);
    if (source !== "price" && (live == null || !isFinite(live))) {
      toast(
        "Loading history for " +
          indicatorLabel(source) +
          "… Keep the alert — it will arm when bars arrive (open the symbol once if it stays empty).",
        "warn"
      );
    }
    const alert: PriceAlert = {
      id: uid(),
      symbol,
      price,
      cond: (condSel.value as AlertCond) || "cross_up",
      freq: (freqSel.value as AlertFreq) || "once",
      source,
      rsiPeriod: 14,
      name: String(nameInput?.value || "").trim(),
      createdAt: Date.now(),
      active: true,
      triggerCount: 0,
    };
    alerts.unshift(alert);
    saveAlerts(alerts);
    drawLine(alert);
    ensureNotifyPermission();
    toast(
      "Alert created · " +
        symbol +
        " · " +
        sourceLabel(alert) +
        " " +
        condLabel(alert.cond) +
        " " +
        formatLevel(alert) +
        (live != null && isFinite(live)
          ? " · live " + (source === "price" ? formatPx(live) : String(Math.round(live * 10000) / 10000))
          : ""),
      "ok"
    );
    setTab("active");
    renderList();
    refreshBadge();
    updateNotifyStatus();
    scheduleSync(0);
  }

  function deleteAlert(id: string): void {
    const a = alerts.find((x) => x.id === id);
    if (a) removeLine(a);
    alerts = alerts.filter((x) => x.id !== id);
    saveAlerts(alerts);
    renderList();
    refreshBadge();
    scheduleSync(0);
  }

  function fire(alert: PriceAlert, value: number): void {
    const now = Date.now();
    if (lastFireAt[alert.id] && now - lastFireAt[alert.id] < 2500) return;
    if (alert.freq === "once" && alert.triggeredAt && sameDay(alert.triggeredAt, now)) return;
    lastFireAt[alert.id] = now;
    alert.triggerCount = (alert.triggerCount || 0) + 1;
    alert.triggeredAt = now;
    const src = normalizeSource(alert.source);
    const msg =
      sourceLabel(alert) +
      " " +
      condLabel(alert.cond) +
      " " +
      formatLevel(alert) +
      " · now " +
      (src === "price" ? formatPx(value) : String(Math.round(value * 10000) / 10000));
    log.unshift({
      id: uid(),
      alertId: alert.id,
      symbol: alert.symbol,
      price: alert.price,
      ltp: value,
      cond: alert.cond,
      source: alert.source,
      at: now,
      message: msg,
    });
    saveLog(log);
    saveAlerts(alerts);
    beep();
    toast(alert.symbol + " · " + msg, "ok");
    notifyBrowser("🔔 " + alert.symbol + (alert.name ? " · " + alert.name : ""), msg, "bc-alert-" + alert.id);
    scheduleSync();
    renderList();
    renderLog();
    refreshBadge();
  }

  /** Test toast + system notification without creating an alert. */
  function runTestAlert(): void {
    const sym = (symInput?.value || deps.getActiveSymbol() || "TEST").trim().toUpperCase();
    const src = normalizeSource(srcSel?.value || "price");
    const level = priceInput ? Number(priceInput.value) : NaN;
    const live = currentValue(sym, src);
    const msg =
      "TEST · " +
      indicatorLabel(src) +
      (isFinite(level) ? " @ " + level : "") +
      (live != null ? " · live " + (Math.round(live * 10000) / 10000) : "") +
      " · " +
      new Date().toLocaleTimeString();
    ensureNotifyPermission();
    beep();
    toast(sym + " · " + msg, "info");
    notifyBrowser("🔔 Test · " + sym, msg);
    updateNotifyStatus();
    if ("Notification" in window && Notification.permission !== "granted") {
      toast("Allow notifications when your browser asks, then test again", "warn");
    }
  }

  function evalHit(cond: AlertCond, prev: number | undefined, curr: number, level: number): boolean {
    if (cond === "cross_up") return prev != null && prev <= level && curr > level;
    if (cond === "cross_down") return prev != null && prev >= level && curr < level;
    if (cond === "above") return curr >= level && (prev == null || prev < level);
    if (cond === "below") return curr <= level && (prev == null || prev > level);
    return false;
  }

  function check(symbol: string, ltp: number): void {
    const key = String(symbol).toUpperCase();
    const bars = barsFor(deps, symbol);

    alerts.forEach(function (a) {
      if (!a.active) return;
      if (String(a.symbol).toUpperCase() !== key) return;

      const src = normalizeSource(a.source);
      const value = evalIndicator(src, bars, ltp > 0 ? ltp : undefined);
      if (value == null || !isFinite(value)) return;

      const pk = key + "|" + src;
      const prev = prevVal[pk];
      prevVal[pk] = value;
      if (evalHit(a.cond, prev, value, a.price)) fire(a, value);
    });
  }

  populateSources();

  document.getElementById("cxAlertsBtn")?.addEventListener("click", function () {
    ensureNotifyPermission();
    openPanel("active");
  });
  document.getElementById("cxAlertsClose")?.addEventListener("click", closePanel);
  backdrop?.addEventListener("click", closePanel);
  document.querySelectorAll("[data-alert-tab]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      setTab((btn as HTMLElement).getAttribute("data-alert-tab") || "create");
    });
  });
  form?.addEventListener("submit", function (e) {
    e.preventDefault();
    createFromForm();
  });
  srcSel?.addEventListener("change", syncValueUi);
  symInput?.addEventListener("change", updateLiveVal);
  symInput?.addEventListener("blur", updateLiveVal);
  useBtn?.addEventListener("click", function () {
    const src = normalizeSource(srcSel?.value || "price");
    if (!priceInput) return;
    const sym = symInput?.value || deps.getActiveSymbol();
    const v = currentValue(sym, src);
    if (v != null && isFinite(v)) {
      priceInput.value = String(Math.round(v * 10000) / 10000);
      updateLiveVal();
      return;
    }
    const def = indicatorDef(src);
    if (def && def.defaultLevel != null) priceInput.value = String(def.defaultLevel);
  });
  document.getElementById("cxAlertEnableNotify")?.addEventListener("click", function () {
    ensureNotifyPermission();
    setTimeout(updateNotifyStatus, 400);
  });
  document.getElementById("cxAlertTest")?.addEventListener("click", function () {
    runTestAlert();
  });
  listEl?.addEventListener("click", function (e) {
    const t = e.target as HTMLElement;
    const row = t.closest(".cx-alert-row") as HTMLElement | null;
    if (!row) return;
    const id = row.getAttribute("data-id") || "";
    const act = t.getAttribute("data-act");
    const a = alerts.find((x) => x.id === id);
    if (!a) return;
    if (act === "del") deleteAlert(id);
    if (act === "goto") {
      closePanel();
      deps.openSymbol(a.symbol);
      setTimeout(function () {
        drawLine(a);
      }, 600);
    }
  });
  logEl?.addEventListener("click", function (e) {
    const t = e.target as HTMLElement;
    const act = t.getAttribute("data-act");
    if (act !== "log-del") return;
    const row = t.closest("[data-log-id]") as HTMLElement | null;
    if (!row) return;
    deleteLogEntry(row.getAttribute("data-log-id") || "");
  });
  document.getElementById("cxAlertsLogClear")?.addEventListener("click", function () {
    clearLog();
  });
  document.getElementById("cxMobileAlertsBtn")?.addEventListener("click", function () {
    openPanel("active");
  });

  // Refresh live value while create pane is open
  window.setInterval(function () {
    if (panel && panel.classList.contains("is-open")) updateLiveVal();
  }, 2000);

  // Re-check all active alerts periodically (covers indicator sources when ticks are sparse).
  window.setInterval(function () {
    const seen: Record<string, boolean> = {};
    alerts.forEach(function (a) {
      if (!a.active) return;
      const sym = String(a.symbol || "").toUpperCase();
      if (!sym || seen[sym]) return;
      seen[sym] = true;
      if (normalizeSource(a.source) !== "price" && deps.ensureBars) {
        try {
          deps.ensureBars(sym);
        } catch {
          /* ignore */
        }
      }
      const ltp = deps.getLtp(sym);
      if (ltp > 0) check(sym, ltp);
      else if (normalizeSource(a.source) !== "price") {
        // Indicator can still move from bar history alone
        check(sym, 0);
      }
    });
  }, 4000);

  window.addEventListener("bc-push-ready", function () {
    scheduleSync(0);
  });
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") scheduleSync(300);
  });
  window.setInterval(function () {
    scheduleSync(0);
  }, 5 * 60 * 1000);
  scheduleSync(2500);

  syncValueUi();
  refreshBadge();
  updateNotifyStatus();
  setTab("create");

  return {
    onQuote: check,
    openCreate,
    redrawLines,
    refreshBadge,
  };
}

function escapeHtml(s: string): string {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatPx(n: number): string {
  if (!(n > 0) && n !== 0) return "—";
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return n.toFixed(2);
}

// Re-export for any external callers
export { calcRsi } from "./indicators";
